import { useTranslation } from './TranslationProvider';
import { SupportedLanguage } from './types';

/**
 * Enum of all Right-to-Left (RTL) languages
 */
export enum RTLLanguage {
  ARABIC = 'ar',
  HEBREW = 'he',
  URDU = 'ur',
  PERSIAN = 'fa',
  PASHTO = 'ps',
}

/**
 * Set of RTL language codes for efficient lookup
 */
const RTL_LANGUAGES = new Set<string>(Object.values(RTLLanguage));

/**
 * Check if a given language is RTL
 * @param language - The language code to check
 * @returns true if the language is RTL, false otherwise
 */
export const isRTLLanguage = (language: SupportedLanguage): boolean => {
  return RTL_LANGUAGES.has(language);
};

/**
 * Get the rotation class for RTL languages
 * @param language - The language code
 * @returns 'rotate-180' for RTL languages, empty string otherwise
 */
export const getRTLRotationClass = (language: SupportedLanguage): string => {
  return isRTLLanguage(language) ? 'rotate-180' : '';
};

/**
 * Get the flex direction class for RTL languages
 * @param language - The language code
 * @returns 'flex-row-reverse' for RTL languages, 'flex-row' otherwise
 */
export const getRTLFlexDirection = (language: SupportedLanguage): string => {
  return isRTLLanguage(language) ? 'flex-row-reverse' : 'flex-row';
};

/**
 * Get position (left/right) based on RTL
 * @param language - The language code
 * @returns 'left' for RTL languages, 'right' otherwise
 */
export const getRTLPosition = (
  language: SupportedLanguage
): 'left' | 'right' => {
  return isRTLLanguage(language) ? 'left' : 'right';
};

/**
 * Get opposite position (right/left) based on RTL
 * @param language - The language code
 * @returns 'right' for RTL languages, 'left' otherwise
 */
export const getRTLOppositePosition = (
  language: SupportedLanguage
): 'left' | 'right' => {
  return isRTLLanguage(language) ? 'right' : 'left';
};

/**
 * React hook to get RTL status and helper functions for the current language
 */
export const useRTL = () => {
  const { currentLanguage } = useTranslation();

  return {
    isRTL: isRTLLanguage(currentLanguage),
    currentLanguage,
    getRotationClass: () => getRTLRotationClass(currentLanguage),
    getFlexDirection: () => getRTLFlexDirection(currentLanguage),
    getPosition: () => getRTLPosition(currentLanguage),
    getOppositePosition: () => getRTLOppositePosition(currentLanguage),
  };
};
